import type { InventoryDiscrepancySkuContextRow } from "./inventoryDiscrepancyApi";
import {
  isExcludedFromSkuContextRunningTotal,
  SKU_CONTEXT_EMPTY_RUNNING_TOTAL,
  type InventoryDiscrepancySkuContextRowWithRunningTotal,
} from "./inventoryDiscrepancySkuContext";

export type SkuContextBalanceEffect = {
  ledger: number;
  warehouse: number;
  floor: number;
  receiving: number;
};

export type SkuContextBalanceAnchors = SkuContextBalanceEffect;

export type InventoryDiscrepancySkuContextRowWithBalances =
  InventoryDiscrepancySkuContextRowWithRunningTotal & {
    covered_receipt_quantity?: number | null;
    ledger_total: number | null;
    warehouse_total: number | null;
    floor_total: number | null;
    receiving_total: number | null;
  };

const TRANSFER_NOT_YET_SHIPPED = new Set(["pending", "packing", "ready_to_ship"]);
const TRANSFER_SHIPPED = new Set(["shipped", "delivered", "partially_received", "received"]);

function kindOf(kind: string): string {
  return kind.trim().toLowerCase();
}

function detailOf(detail: string | null): string {
  return (detail ?? "").trim().toLowerCase().replace(/\s+/g, "_");
}

function isHubTransfer(kind: string): boolean {
  const normalized = kindOf(kind);
  return normalized === "transfer_in" || normalized === "transfer_out";
}

function signedPacked(kind: string, packedQuantity: number): number {
  const packed = Math.max(0, Math.trunc(packedQuantity));
  if (packed === 0) {
    return 0;
  }
  return kindOf(kind) === "transfer_out" ? -packed : packed;
}

function zeros(): SkuContextBalanceEffect {
  return { ledger: 0, warehouse: 0, floor: 0, receiving: 0 };
}

/**
 * Signed move for one history row.
 * Null when the row is cancelled or pending and should not change the balances.
 * Purchase orders stay on the page and do not move any balance.
 * A packed transfer that has not shipped does not move warehouse yet.
 */
export function skuContextBalanceEffect(
  row: Pick<
    InventoryDiscrepancySkuContextRowWithBalances,
    "kind" | "quantity" | "detail" | "packed_quantity" | "covered_receipt_quantity"
  >,
): SkuContextBalanceEffect | null {
  if (isExcludedFromSkuContextRunningTotal(row.detail)) {
    return null;
  }
  const kind = kindOf(row.kind);
  const quantity = row.quantity ?? 0;
  if (kind === "purchase_order") {
    return zeros();
  }
  if (kind === "inbound_receipt") {
    return { ledger: 0, warehouse: 0, floor: 0, receiving: quantity };
  }
  if (kind === "purchase_invoice") {
    const covered = Math.max(0, Math.trunc(row.covered_receipt_quantity ?? 0));
    return { ledger: quantity, warehouse: quantity, floor: 0, receiving: covered === 0 ? 0 : -covered };
  }
  if (kind === "work_order_allocation" || kind === "transfer_pack") {
    const moved = Math.max(0, Math.trunc(kind === "transfer_pack" ? (row.packed_quantity ?? row.quantity ?? 0) : (row.quantity ?? 0)));
    if (moved === 0) {
      return zeros();
    }
    return { ledger: 0, warehouse: -moved, floor: moved, receiving: 0 };
  }
  if (kind === "work_order_consumption") {
    const consumed = Math.max(0, Math.trunc(quantity));
    return {
      ledger: -consumed,
      warehouse: 0,
      floor: -consumed,
      receiving: 0,
    };
  }
  if (kind === "work_order") {
    return { ledger: quantity, warehouse: quantity, floor: 0, receiving: 0 };
  }
  if (isHubTransfer(kind)) {
    const packed = row.packed_quantity;
    if (packed == null || !Number.isFinite(packed)) {
      const signed = kind === "transfer_out" ? -quantity : quantity;
      return { ledger: signed, warehouse: signed, floor: 0, receiving: 0 };
    }
    const detail = detailOf(row.detail);
    if (TRANSFER_NOT_YET_SHIPPED.has(detail) || !TRANSFER_SHIPPED.has(detail)) {
      return zeros();
    }
    const signed = signedPacked(kind, packed);
    if (kind === "transfer_out") {
      return { ledger: signed, warehouse: 0, floor: signed, receiving: 0 };
    }
    return { ledger: signed, warehouse: signed, floor: 0, receiving: 0 };
  }
  if (kind === "adjustment" || kind === "wastage" || kind === "prototype") {
    return { ledger: quantity, warehouse: quantity, floor: 0, receiving: 0 };
  }
  if (kind === "sales_order") {
    return { ledger: -quantity, warehouse: -quantity, floor: 0, receiving: 0 };
  }
  return zeros();
}

/** Quantity cell: R is receiving, F is a hub pick, a bare number is a ledger quantity. */
export function formatSkuContextBalanceQuantity(
  row: Pick<
    InventoryDiscrepancySkuContextRowWithBalances,
    "kind" | "quantity" | "detail" | "packed_quantity"
  >,
): string {
  const kind = kindOf(row.kind);
  if (kind === "inbound_receipt") {
    if (row.quantity == null) {
      return SKU_CONTEXT_EMPTY_RUNNING_TOTAL;
    }
    return `${String(row.quantity)}R`;
  }
  if (kind === "work_order_allocation" || kind === "transfer_pack") {
    const moved = Math.max(
      0,
      Math.trunc(kind === "transfer_pack" ? (row.packed_quantity ?? row.quantity ?? 0) : (row.quantity ?? 0)),
    );
    if (moved > 0) {
      return `${String(moved)}F`;
    }
  }
  if (kind === "work_order_consumption") {
    if (row.quantity == null) {
      return SKU_CONTEXT_EMPTY_RUNNING_TOTAL;
    }
    const consumed = Math.max(0, Math.trunc(row.quantity));
    return consumed === 0 ? "0" : `-${String(consumed)}`;
  }
  if (isHubTransfer(kind) && row.packed_quantity != null && Number.isFinite(row.packed_quantity)) {
    const signed = signedPacked(kind, row.packed_quantity);
    const label = signed > 0 ? `+${String(signed)}` : String(signed);
    if (TRANSFER_NOT_YET_SHIPPED.has(detailOf(row.detail))) {
      return `(${label})`;
    }
    return label;
  }
  if (row.quantity == null) {
    return SKU_CONTEXT_EMPTY_RUNNING_TOTAL;
  }
  const signed = kind === "sales_order" || kind === "transfer_out" ? -row.quantity : row.quantity;
  if (signed > 0) {
    return `+${String(signed)}`;
  }
  return String(signed);
}

/**
 * Newest row shows the current anchors. Older rows undo each event.
 * Cancelled and pending rows keep a blank balance and do not move the walk.
 */
export function attachSkuContextBalanceColumns<T extends InventoryDiscrepancySkuContextRow>(
  rows: T[],
  anchors: SkuContextBalanceAnchors,
): Array<T & InventoryDiscrepancySkuContextRowWithBalances> {
  let ledger = anchors.ledger;
  let warehouse = anchors.warehouse;
  let floor = anchors.floor;
  let receiving = anchors.receiving;
  return rows.map((row) => {
    const effect = skuContextBalanceEffect(row);
    const priorTotals = row as T & Partial<InventoryDiscrepancySkuContextRowWithRunningTotal>;
    if (effect == null) {
      return {
        ...row,
        on_hand_total: priorTotals.on_hand_total ?? null,
        in_house_total: priorTotals.in_house_total ?? null,
        ledger_total: null,
        warehouse_total: null,
        floor_total: null,
        receiving_total: null,
      };
    }
    const withBalances = {
      ...row,
      on_hand_total: priorTotals.on_hand_total ?? null,
      in_house_total: priorTotals.in_house_total ?? null,
      ledger_total: ledger,
      warehouse_total: warehouse,
      floor_total: floor,
      receiving_total: receiving,
    };
    ledger -= effect.ledger;
    warehouse -= effect.warehouse;
    floor -= effect.floor;
    receiving -= effect.receiving;
    return withBalances;
  });
}
